import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Star, Quote } from "lucide-react";

export default function TestimonialsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const testimonials = [
    {
      name: "Mette Hansen",
      location: "Frederiksberg",
      text: "Vores lejlighed blev solgt på kun 2 uger efter renoveringen - og til en pris langt over forventning. Fantastisk professionelt arbejde!",
      rating: 5,
      increase: "+18%",
    },
    {
      name: "Lars Pedersen",
      location: "Hellerup",
      text: "Salgsrenovering forstod præcis hvad der skulle til. Køkkenet og badeværelset blev totalt forvandlet, og det kunne ses på salgsprisen.",
      rating: 5,
      increase: "+22%",
    },
    {
      name: "Anne Christensen",
      location: "Østerbro",
      text: "Professionel rådgivning fra start til slut. De anbefalede kun de renoveringer der gav mening - ingen unødvendige udgifter.",
      rating: 5,
      increase: "+15%",
    },
  ];

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 bg-secondary/30"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-xs font-semibold uppercase tracking-widest text-accent">
            Kundeudtalelser
          </span>
          <h2 
            className="mt-4 text-4xl md:text-5xl font-bold text-foreground tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Hvad vores kunder siger
          </h2>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white p-8 border border-border relative"
              data-testid={`testimonial-card-${index}`}
            >
              {/* Quote Icon */}
              <Quote className="w-10 h-10 text-secondary mb-4" />
              
              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                ))}
              </div>

              {/* Text */}
              <p className="text-muted-foreground leading-relaxed mb-6">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-foreground">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {testimonial.location}
                  </div>
                </div>
                {/* Value Increase Badge */}
                <div className="bg-primary/10 px-3 py-1">
                  <span className="text-sm font-bold text-primary">
                    {testimonial.increase}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
